/* First run: the person chooses an empty start, the sample set, or an import. Nothing is saved before that choice. */
import { parseBackup } from "../domain/backup";
import { addWorkspace, cloneLibrary } from "../domain/library";
import { emptyLibrary, type Library } from "../domain/types";
import { readPrototypeData } from "../storage/local-storage";
import { addSampleData } from "../sample/sample-data";
import { ctx } from "./context";
import { ic } from "./icons";
import { openImportSheet, refreshSafety, safety, showImportPreview, undoLastImport } from "./import-export";
import { toast } from "./shell";
import { failNotice } from "./failure";
import { topBar } from "./app";
import { dmyhm, esc } from "../domain/format";

/** the empty start is built once per account and re-sent on retry, so a lost reply cannot create two workspaces */
let pendingBlank: { owner: string; lib: Library } | null = null;
function blankOnce(): Library {
  const owner = ctx.account?.id ?? "local";
  if (!pendingBlank || pendingBlank.owner !== owner) pendingBlank = { owner, lib: startBlankLibrary() };
  return pendingBlank.lib;
}

export function startBlankLibrary(base: Library = emptyLibrary()): Library {
  const lib = cloneLibrary(base);
  addWorkspace(lib, { name: "สถานที่ของฉัน", icon: "home" });
  return lib;
}

export function renderWelcome($app: HTMLElement) {
  const proto = readPrototypeData();
  $app.innerHTML = topBar()
    + `<section class="welcome">
        <div class="welcome-keys" aria-hidden="true"><span class="key" style="--g:#A78BFA"><span class="face"></span>${ic("link")}</span><span class="key" style="--g:#34D399"><span class="face"></span>${ic("note")}</span><span class="key" style="--g:#38BDF8"><span class="face"></span>${ic("grid")}</span></div>
        <h2>เริ่มใช้ MEMOLINK</h2>
        <p>รวมลิงก์และโน้ตสำคัญเป็นหมวด เลือกสีและไอคอนให้จำง่าย แตะเพื่อเปิดลิงก์หรืออ่านโน้ตได้ทันที</p>
        ${ctx.store.loadIssue ? `<p class="note"><span class="tag bad">แจ้งเตือน</span> ${ctx.store.loadIssue}</p>` : ""}
        <div class="stack">
          <button class="btn primary" data-do="welcome-blank" id="btn-start-blank">${ic("plus")}เริ่มด้วยสถานที่ว่าง</button>
          <button class="btn" data-do="welcome-sample" id="btn-start-sample">${ic("sparkles")}ลองด้วยข้อมูลตัวอย่าง</button>
          ${ctx.mode === "cloud" && ctx.localOffer ? `<button class="btn" data-do="migrate-open" id="btn-start-migrate">${ic("check")}ย้ายข้อมูลในเครื่องนี้เข้าบัญชี</button>` : ""}
          <button class="btn" data-do="welcome-import" id="btn-start-import">${ic("folder")}นำเข้าจากไฟล์สำรอง / ต้นแบบ</button>
          ${proto ? `<button class="btn" data-do="welcome-proto" id="btn-start-proto">${ic("check")}นำเข้าข้อมูลต้นแบบที่พบในเบราว์เซอร์นี้</button>` : ""}
          ${safety ? `<button class="btn ghost" data-do="welcome-undo" id="btn-start-undo">${ic("left")}กู้ข้อมูล${safety.reason} (${dmyhm(safety.savedAt)})</button>` : ""}
        </div>
        <p class="note" style="margin-top:14px">ข้อมูลตัวอย่างลบออกได้ภายหลังใน ตั้งค่า โดยไม่กระทบรายการของคุณ · ${ctx.mode === "cloud" && ctx.account ? `ข้อมูลเก็บในบัญชี ${esc(ctx.account.email)} บน Cloud เปิดเครื่องอื่นด้วยบัญชีเดียวกันได้` : "ข้อมูลเก็บในเบราว์เซอร์นี้ ยังไม่ซิงก์ข้ามเครื่อง"}</p>
      </section>`;
}

export function handleWelcomeAction(act: string, _el: HTMLElement): boolean {
  const { store, V } = ctx;
  const go = async (lib: Library, msg: string) => {
    const r = await store.replace(lib);
    if (!r.ok) { failNotice(r); return; } // stay on the first-run screen
    if (lib === pendingBlank?.lib) pendingBlank = null;
    V.view = "home"; ctx.render(); toast(msg);
  };
  switch (act) {
    case "welcome-blank": void go(blankOnce(), "พร้อมใช้งาน เพิ่มหมวดแรกได้เลย"); return true;
    case "welcome-sample": void go(addSampleData(emptyLibrary()), "เพิ่มข้อมูลตัวอย่างแล้ว ลบได้ในตั้งค่า"); return true;
    case "welcome-import": openImportSheet(); return true;
    case "welcome-proto": { const raw = readPrototypeData(); if (raw) showImportPreview(parseBackup(raw), "ต้นแบบในเบราว์เซอร์นี้"); return true; }
    case "welcome-undo": void undoLastImport().then(refreshSafety); return true;
  }
  return false;
}
